import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  ChevronLeft, Building2, Loader2, CheckCircle, Clock, AlertCircle,
  Send, Eye, Calendar, Plus, User, PoundSterling, FileText, Shield,
  Home, Rocket, MessageSquare, ThumbsUp, ThumbsDown, Phone, Mail
} from "lucide-react";
import { API } from "@/App";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700", icon: FileText },
  ready_for_sale: { label: "Ready for Sale", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
  ready_to_market: { label: "Ready to Market", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
  live: { label: "Live", color: "bg-green-100 text-green-700", icon: Rocket },
  under_offer: { label: "Under Offer", color: "bg-amber-100 text-amber-700", icon: Clock },
  let_agreed: { label: "Let Agreed", color: "bg-amber-100 text-amber-700", icon: Clock },
  exchanged: { label: "Exchanged", color: "bg-purple-100 text-purple-700", icon: CheckCircle },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  withdrawn: { label: "Withdrawn", color: "bg-red-100 text-red-700", icon: AlertCircle }
};

const CHECKLIST_LABELS = {
  ids_uploaded: "Seller ID Uploaded",
  contract_signed: "Contract Signed",
  epc_uploaded: "EPC Certificate",
  draft_approved: "Draft Details Approved",
  leasehold_pack_complete: "Leasehold Pack Complete",
  agreement_signed: "Landlord Agreement Signed",
  gas_safety_uploaded: "Gas Safety Certificate",
  eicr_uploaded: "EICR Certificate"
};

export const ListingDetailPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);
  const [viewings, setViewings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Modals
  const [showViewingModal, setShowViewingModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [sendingPortalInvite, setSendingPortalInvite] = useState(false);
  const [goingLive, setGoingLive] = useState(false);
  
  // Viewing form
  const [viewingForm, setViewingForm] = useState({
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    date: "",
    time: "",
    notes: "",
    send_approval_request: true
  });
  
  // Offer form
  const [offerForm, setOfferForm] = useState({
    contact_name: "",
    offer_amount: "",
    offer_mode: "show_figure",
    buyer_position: "",
    finance_type: "mortgage",
    mortgage_agreed: false,
    notes: ""
  });

  const fetchListing = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/listings/${listingId}`, { headers });
      setListing(res.data?.listing);
      setViewings(res.data?.viewings || []);
      setOffers(res.data?.offers || []);
    } catch (error) {
      console.error("Error fetching listing:", error);
      toast.error("Failed to load listing");
    } finally {
      setLoading(false);
    }
  }, [token, listingId]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const handleSendPortalInvite = async () => {
    setSendingPortalInvite(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/listings/${listingId}/send-portal-invite`, {}, { headers });
      toast.success("Portal invite sent successfully");
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send portal invite");
    } finally {
      setSendingPortalInvite(false);
    }
  };

  const handleChecklistUpdate = async (item, completed) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/listings/${listingId}/checklist`, {
        listing_id: listingId,
        item,
        completed
      }, { headers });
      fetchListing();
    } catch (error) {
      toast.error("Failed to update checklist");
    }
  };

  const handleGoLive = async () => {
    setGoingLive(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/listings/${listingId}/go-live`, {}, { headers });
      toast.success("Property is now LIVE on the market!");
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cannot go live - check the checklist");
    } finally {
      setGoingLive(false);
    }
  };

  const handleCreateViewing = async () => {
    if (!viewingForm.contact_name || !viewingForm.date || !viewingForm.time) {
      toast.error("Please fill in viewer name, date and time");
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/viewings`, {
        listing_id: listingId,
        contact_id: "",
        ...viewingForm
      }, { headers });
      toast.success("Viewing created");
      setShowViewingModal(false);
      setViewingForm({ contact_name: "", contact_phone: "", contact_email: "", date: "", time: "", notes: "", send_approval_request: true });
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create viewing");
    }
  };

  const handleCreateOffer = async () => {
    if (!offerForm.contact_name) {
      toast.error("Please enter the buyer's name");
      return;
    }
    
    if (offerForm.offer_mode === "show_figure" && !offerForm.offer_amount) {
      toast.error("Please enter the offer amount");
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/offers`, {
        listing_id: listingId,
        contact_id: "",
        ...offerForm,
        offer_amount: offerForm.offer_amount ? parseInt(offerForm.offer_amount) : null
      }, { headers });
      toast.success("Offer submitted");
      setShowOfferModal(false);
      setOfferForm({ contact_name: "", offer_amount: "", offer_mode: "show_figure", buyer_position: "", finance_type: "mortgage", mortgage_agreed: false, notes: "" });
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit offer");
    }
  };

  const handleOfferResponse = async (offerId, response, counterAmount = null) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/offers/${offerId}/respond`, {
        offer_id: offerId,
        response,
        counter_amount: counterAmount
      }, { headers });
      toast.success(`Offer ${response}`);
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to respond to offer");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Listing not found</p>
      </div>
    );
  }

  const isSales = listing.listing_type === "sales";
  const statusConfig = STATUS_CONFIG[listing.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  
  // Calculate checklist progress
  const checklist = listing.checklist || {};
  const checklistItems = Object.keys(checklist);
  const completedItems = checklistItems.filter(k => checklist[k]);
  const checklistProgress = checklistItems.length > 0 ? (completedItems.length / checklistItems.length) * 100 : 0;
  const isReadyForLive = checklistProgress === 100;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className={`border-b ${isSales ? "bg-gradient-to-r from-blue-600 to-blue-700" : "bg-gradient-to-r from-purple-600 to-purple-700"} text-white`}>
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 text-white/80 hover:text-white hover:bg-white/10">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge className={`${statusConfig.color} px-3 py-1`}>
                  <StatusIcon className="w-4 h-4 mr-1" />
                  {statusConfig.label}
                </Badge>
                <Badge variant="outline" className="text-white border-white/40">
                  {isSales ? "For Sale" : "To Let"}
                </Badge>
              </div>
              <h1 className="text-2xl font-bold">{listing.property?.full_address}</h1>
              <p className="text-white/70 mt-1">{listing.contact_name}</p>
            </div>
            <div className="text-right">
              <p className="text-white/70 text-sm">{isSales ? "Asking Price" : "Rent PCM"}</p>
              <p className="text-3xl font-bold">
                {listing.price_display || listing.rent_display}
              </p>
              <div className="flex items-center gap-4 mt-2 text-white/80">
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {listing.viewings_count || 0} viewings
                </span>
                <span className="flex items-center gap-1">
                  <PoundSterling className="w-4 h-4" />
                  {listing.offers_count || 0} offers
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="checklist">
              Checklist
              {!isReadyForLive && <Badge className="ml-2 bg-amber-500 text-white">{completedItems.length}/{checklistItems.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="viewings">
              Viewings
              {viewings.length > 0 && <Badge className="ml-2">{viewings.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="offers">
              Offers
              {offers.length > 0 && <Badge className="ml-2 bg-amber-500 text-white">{offers.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Quick Actions */}
              <div className="lg:col-span-2 space-y-6">
                {/* Action Cards */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Portal Invite */}
                  <Card className={listing.portal_invite_sent ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            <Send className="w-4 h-4" />
                            Portal Invite
                          </h3>
                          <p className="text-sm text-slate-600 mt-1">
                            {listing.portal_invite_sent 
                              ? `Sent ${new Date(listing.portal_invite_sent_at).toLocaleDateString()}`
                              : "Send login to seller/landlord"}
                          </p>
                        </div>
                        {!listing.portal_invite_sent && (
                          <Button size="sm" onClick={handleSendPortalInvite} disabled={sendingPortalInvite}>
                            {sendingPortalInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
                          </Button>
                        )}
                        {listing.portal_invite_sent && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Go Live */}
                  <Card className={listing.status === "live" ? "border-green-200 bg-green-50" : isReadyForLive ? "border-emerald-200 bg-emerald-50" : ""}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            <Rocket className="w-4 h-4" />
                            Go Live
                          </h3>
                          <p className="text-sm text-slate-600 mt-1">
                            {listing.status === "live" 
                              ? `Live since ${new Date(listing.live_at).toLocaleDateString()}`
                              : isReadyForLive 
                                ? "Ready to go on market!"
                                : "Complete checklist first"}
                          </p>
                        </div>
                        {listing.status === "draft" && isReadyForLive && (
                          <Button size="sm" onClick={handleGoLive} disabled={goingLive} className="bg-emerald-600 hover:bg-emerald-700">
                            {goingLive ? <Loader2 className="w-4 h-4 animate-spin" /> : "Go Live"}
                          </Button>
                        )}
                        {listing.status === "live" && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Property Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Home className="w-5 h-5" />
                      Property Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Type</p>
                        <p className="font-medium capitalize">{listing.property?.property_type?.replace(/_/g, " ")}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Bedrooms</p>
                        <p className="font-medium">{listing.property?.bedrooms}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Bathrooms</p>
                        <p className="font-medium">{listing.property?.bathrooms}</p>
                      </div>
                      {isSales && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-500">Tenure</p>
                          <p className="font-medium capitalize">{listing.property?.tenure}</p>
                        </div>
                      )}
                      {listing.epc?.rating && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-500">EPC Rating</p>
                          <p className="font-medium">{listing.epc.rating}</p>
                        </div>
                      )}
                    </div>
                    
                    {listing.description && (
                      <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500 mb-1">Description</p>
                        <p className="text-slate-700 whitespace-pre-wrap">{listing.description}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Checklist Progress */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Ready for Market</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>{completedItems.length} of {checklistItems.length} complete</span>
                        <span className="font-medium">{Math.round(checklistProgress)}%</span>
                      </div>
                      <Progress value={checklistProgress} className="h-2" />
                    </div>
                    {!isReadyForLive && (
                      <Button variant="link" className="px-0 mt-2" onClick={() => setActiveTab("checklist")}>
                        View checklist →
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Stats */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Activity</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="flex items-center gap-2 text-sm">
                        <Eye className="w-4 h-4 text-slate-500" />
                        Viewings
                      </span>
                      <span className="font-semibold">{viewings.length}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="flex items-center gap-2 text-sm">
                        <PoundSterling className="w-4 h-4 text-slate-500" />
                        Offers
                      </span>
                      <span className="font-semibold">{offers.length}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Contact */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{isSales ? "Seller" : "Landlord"}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium">{listing.contact_name}</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-3"
                      onClick={() => navigate(`/contacts/${listing.contact_id}`)}
                    >
                      View Contact
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Checklist Tab */}
          <TabsContent value="checklist">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  {isSales ? "Ready for Sale Checklist" : "Ready to Market Checklist"}
                </CardTitle>
                <CardDescription>
                  Complete all items before the property can go live
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {checklistItems.map(item => (
                    <div 
                      key={item}
                      className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                        checklist[item] ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200"
                      }`}
                    >
                      <Checkbox
                        checked={checklist[item]}
                        onCheckedChange={(checked) => handleChecklistUpdate(item, checked)}
                        className="w-5 h-5"
                      />
                      <div className="flex-1">
                        <p className={`font-medium ${checklist[item] ? "text-green-700" : "text-slate-700"}`}>
                          {CHECKLIST_LABELS[item] || item.replace(/_/g, " ")}
                        </p>
                      </div>
                      {checklist[item] && <CheckCircle className="w-5 h-5 text-green-600" />}
                    </div>
                  ))}
                </div>
                
                {isReadyForLive && listing.status === "draft" && (
                  <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-emerald-800">All items complete!</h3>
                        <p className="text-sm text-emerald-600">This property is ready to go live on the market.</p>
                      </div>
                      <Button onClick={handleGoLive} disabled={goingLive} className="bg-emerald-600 hover:bg-emerald-700">
                        {goingLive ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                        Go Live Now
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Viewings Tab */}
          <TabsContent value="viewings">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Viewings</CardTitle>
                  <CardDescription>Manage property viewings</CardDescription>
                </div>
                <Button onClick={() => setShowViewingModal(true)} data-testid="add-viewing-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Viewing
                </Button>
              </CardHeader>
              <CardContent>
                {viewings.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p>No viewings scheduled yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {viewings.map(viewing => (
                      <div key={viewing.viewing_id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-500" />
                              <span className="font-medium">{viewing.contact_name}</span>
                              <Badge className={
                                viewing.status === "confirmed" ? "bg-green-100 text-green-700" :
                                viewing.status === "pending" ? "bg-amber-100 text-amber-700" :
                                viewing.status === "completed" ? "bg-blue-100 text-blue-700" :
                                "bg-slate-100 text-slate-700"
                              }>
                                {viewing.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              {viewing.date} at {viewing.time}
                            </p>
                            {viewing.feedback && (
                              <div className="mt-2 p-2 bg-slate-50 rounded text-sm">
                                <p className="text-slate-600"><strong>Feedback:</strong> {viewing.feedback}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Offers</CardTitle>
                  <CardDescription>Review and respond to offers</CardDescription>
                </div>
                <Button onClick={() => setShowOfferModal(true)} data-testid="add-offer-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Record Offer
                </Button>
              </CardHeader>
              <CardContent>
                {offers.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <PoundSterling className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p>No offers received yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {offers.map(offer => (
                      <div key={offer.offer_id} className={`p-4 border rounded-lg ${
                        offer.status === "accepted" ? "border-green-200 bg-green-50" :
                        offer.status === "rejected" ? "border-red-200 bg-red-50" :
                        "border-amber-200 bg-amber-50"
                      }`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{offer.contact_name}</span>
                              <Badge className={
                                offer.status === "accepted" ? "bg-green-600 text-white" :
                                offer.status === "rejected" ? "bg-red-600 text-white" :
                                offer.status === "countered" ? "bg-purple-600 text-white" :
                                "bg-amber-600 text-white"
                              }>
                                {offer.status}
                              </Badge>
                            </div>
                            {offer.offer_amount && (
                              <p className="text-2xl font-bold mt-1">£{offer.offer_amount.toLocaleString()}</p>
                            )}
                            {offer.buyer_position && (
                              <p className="text-sm text-slate-600 mt-1 capitalize">
                                Position: {offer.buyer_position.replace(/_/g, " ")}
                              </p>
                            )}
                            <p className="text-xs text-slate-500 mt-2">
                              Received: {new Date(offer.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          
                          {offer.status === "made" && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleOfferResponse(offer.offer_id, "rejected")}
                              >
                                <ThumbsDown className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                              <Button 
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleOfferResponse(offer.offer_id, "accepted")}
                              >
                                <ThumbsUp className="w-4 h-4 mr-1" />
                                Accept
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Viewing Modal */}
      <Dialog open={showViewingModal} onOpenChange={setShowViewingModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Viewing</DialogTitle>
            <DialogDescription>Add a new viewing for this property</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Viewer Name *</Label>
              <Input
                value={viewingForm.contact_name}
                onChange={(e) => setViewingForm({...viewingForm, contact_name: e.target.value})}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={viewingForm.contact_phone}
                  onChange={(e) => setViewingForm({...viewingForm, contact_phone: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={viewingForm.contact_email}
                  onChange={(e) => setViewingForm({...viewingForm, contact_email: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={viewingForm.date}
                  onChange={(e) => setViewingForm({...viewingForm, date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={viewingForm.time}
                  onChange={(e) => setViewingForm({...viewingForm, time: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={viewingForm.send_approval_request}
                onCheckedChange={(v) => setViewingForm({...viewingForm, send_approval_request: v})}
              />
              <Label>Send approval request to {isSales ? "seller" : "landlord"}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewingModal(false)}>Cancel</Button>
            <Button onClick={handleCreateViewing}>Create Viewing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Offer Modal */}
      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Offer</DialogTitle>
            <DialogDescription>Add a new offer for this property</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{isSales ? "Buyer" : "Tenant"} Name *</Label>
              <Input
                value={offerForm.contact_name}
                onChange={(e) => setOfferForm({...offerForm, contact_name: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Offer Amount</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                <Input
                  type="number"
                  value={offerForm.offer_amount}
                  onChange={(e) => setOfferForm({...offerForm, offer_amount: e.target.value})}
                  className="pl-7"
                />
              </div>
            </div>
            {isSales && (
              <div>
                <Label>Buyer Position</Label>
                <Select value={offerForm.buyer_position} onValueChange={(v) => setOfferForm({...offerForm, buyer_position: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ftb">First Time Buyer</SelectItem>
                    <SelectItem value="has_property">Has Property to Sell</SelectItem>
                    <SelectItem value="sold_stc">Sold STC</SelectItem>
                    <SelectItem value="nothing_to_sell">Cash Buyer / Nothing to Sell</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={offerForm.notes}
                onChange={(e) => setOfferForm({...offerForm, notes: e.target.value})}
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfferModal(false)}>Cancel</Button>
            <Button onClick={handleCreateOffer}>Submit Offer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ListingDetailPage;
